import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../../Components/SortableTable";
import Title from "../../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  requestErrorHandler,
  tableAlternateColors,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";
import AddRecipe from "./AddRecipe";

const server = EnvSettings.server;

export default function ManufacturingRecipes() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const searchInputRef = useRef(null);
  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [recipeToEdit, setRecipeToEdit] = useState(null);

  const fetchRecipes = useCallback(() => {
    setIsLoading(true);
    setErrorMessage("");
    fetch(server + "/get_manufacturing_recipes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          setRecipes([]);
          setErrorMessage(
            result.message ||
              t(
                "manufacturing.recipesFetchFailed",
                "Failed to fetch manufacturing recipes."
              )
          );
        } else {
          setRecipes(
            Array.isArray(result.manufacturingRecipes)
              ? result.manufacturingRecipes
              : []
          );
          setErrorMessage("");
        }
      })
      .catch(() => {
        setRecipes([]);
        setErrorMessage(
          t(
            "manufacturing.recipesFetchFailed",
            "Failed to fetch manufacturing recipes."
          )
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, t]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  useEffect(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      setFilteredRecipes(recipes);
      return;
    }
    const filtered = recipes.filter((recipe) => {
      const fields = [
        recipe.name,
        recipe.description,
        recipe.serialNumberPrefix,
        recipe.startingSerialNumber?.toString(),
        recipe.nextSerialNumber?.toString(),
        recipe.StockItem?.name,
        recipe.StockItem?.sku,
      ];
      (recipe.ManufacturingRecipeServiceItems || []).forEach((entry) => {
        if (entry?.ServiceItem?.name) {
          fields.push(entry.ServiceItem.name);
        }
        if (entry?.ServiceItem?.sku) {
          fields.push(entry.ServiceItem.sku);
        }
      });
      return fields.some((field) => {
        if (field === undefined || field === null) {
          return false;
        }
        return field.toString().toLowerCase().includes(term);
      });
    });
    setFilteredRecipes(filtered);
  }, [recipes, searchValue]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const componentCount = (recipe) =>
    recipe?.ManufacturingRecipeStockItems?.length ?? 0;
  const serviceItemCount = (recipe) =>
    recipe?.ManufacturingRecipeServiceItems?.length ?? 0;

  const recipeHeadCells = useMemo(
    () => [
      {
        id: "name",
        label: t("manufacturing.recipeName", "Recipe name"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "StockItem.sku",
        label: t("stock.sku", "Resulting Stock Item"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "ManufacturingRecipeStockItems",
        label: t("manufacturing.components", "Number of Components"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "ManufacturingRecipeServiceItems",
        label: t("manufacturing.serviceItems", "Service items"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "nextSerialNumber",
        label: t("manufacturing.nextSerialNumber", "Next serial"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "updatedAt",
        label: t("updatedAt", "Updated"),
        disablePadding: false,
        numeric: false,
      },
    ],
    [t]
  );

  const specialFormatting = useMemo(
    () => [
      {
        id: "ManufacturingRecipeStockItems",
        format: (data, _, recipe) => componentCount(recipe),
      },
      {
        id: "ManufacturingRecipeServiceItems",
        format: (data, _, recipe) => serviceItemCount(recipe),
      },
      {
        id: "updatedAt",
        format: (data) => (data ? formatTimeWithTZOffset(data, ECABIN) : ""),
      },
    ],
    [t]
  );

  return (
    <Grid container spacing={3}>
      <AddRecipe
        open={showAddRecipe}
        onClose={() => {
          setShowAddRecipe(false);
          setRecipeToEdit(null);
        }}
        onCompleted={() => {
          setShowAddRecipe(false);
          setRecipeToEdit(null);
          fetchRecipes();
        }}
        initialRecipe={recipeToEdit}
      />
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/cpanel/manufacturing">
            {t("manufacturing.header", "Manufacturing")}
          </Link>
          <Typography color="textPrimary">
            {t("manufacturing.recipes", "Manufacturing Recipes")}
          </Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setRecipeToEdit(null);
                  setShowAddRecipe(true);
                }}
              >
                {t("manufacturing.addRecipe", "Add Recipe")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Title>
                {t("manufacturing.recipes", "Manufacturing Recipes")}
              </Title>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("common.search", "Search")}
                value={searchValue}
                onChange={handleSearchChange}
                inputRef={searchInputRef}
              />
            </Grid>
          </Grid>
          {errorMessage ? (
            <Grid container style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  style={{ padding: 16, background: "#ffeeee" }}
                >
                  {errorMessage}
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {isLoading ? (
            <CircularProgress />
          ) : filteredRecipes.length ? (
            <SortableTable
              tableLayout="fixed"
              downloadButton
              header={t("manufacturing.recipes", "Manufacturing Recipes")}
              headCells={recipeHeadCells}
              tableData={filteredRecipes}
              rowsPerPage={25}
              defaultSortRow="name"
              alternateColors
              getRowStyle={tableAlternateColors}
              specialDataFormatting={specialFormatting}
              handleRowClick={(recipe) => {
                setRecipeToEdit(recipe);
                setShowAddRecipe(true);
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              {searchValue
                ? t(
                    "manufacturing.noRecipesMatchSearch",
                    "No recipes match your search."
                  )
                : t("manufacturing.noRecipesFound", "No recipes available.")}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
